<script lang="ts">
  import { onMount } from "svelte";
  import type { PublicBridgeEvent } from "../../shared/contracts";
  import { fetchEventPage } from "../client/api";
  import { formatEtTimestamp } from "../format";
  import DrawerShell from "./DrawerShell.svelte";

  export let onclose: () => void;
  export let returnFocus: HTMLElement | null;

  let events: PublicBridgeEvent[] = [];
  let cursor: string | null = null;
  let hasMore = true;
  let loading = false;
  let failed = false;
  let sentinel: HTMLElement;
  let observer: IntersectionObserver | null = null;
  let controller: AbortController | null = null;

  async function load(): Promise<void> {
    if (loading || !hasMore) return;
    loading = true;
    failed = false;
    controller?.abort();
    controller = new AbortController();
    try {
      const page = await fetchEventPage(cursor, controller.signal);
      if (controller.signal.aborted) return;
      const seen = new Set(events.map((event) => event.id));
      events = [...events, ...page.events.filter((event) => {
        if (seen.has(event.id)) return false;
        seen.add(event.id);
        return true;
      })].slice(0, 250);
      cursor = page.nextCursor;
      hasMore = page.hasMore && events.length < 250;
    } catch {
      if (!controller.signal.aborted) failed = true;
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void load();
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver((entries) => {
        if (!failed && entries.some((entry) => entry.isIntersecting)) void load();
      }, { rootMargin: "160px" });
      observer.observe(sentinel);
    }
    return () => {
      observer?.disconnect();
      controller?.abort();
    };
  });
</script>

<DrawerShell title="Bridge history" context="Latest confirmed movements" {onclose} {returnFocus}>
  <p class="drawer-intro">The newest 250 raised and lowered movements appear here as they are confirmed.</p>

  {#if events.length > 0}
    <ol class="event-list">
      {#each events as event (event.id)}
        <li>
          <span class:up={event.state === "up"} class="event-list__mark" aria-hidden="true"></span>
          <div>
            <strong>{event.bridgeName}</strong>
            <span>{event.state === "up" ? "Raised · roadway unavailable" : "Lowered · roadway available"}</span>
          </div>
          <time datetime={event.occurredAt}>{formatEtTimestamp(event.occurredAt)}</time>
        </li>
      {/each}
    </ol>
  {:else if !loading && !failed}
    <div class="drawer-state">
      <strong>No confirmed movements yet</strong>
      <p>New bridge movements will appear here automatically.</p>
    </div>
  {/if}

  {#if loading}
    <div class="drawer-state drawer-state--loading" role="status">Loading movements…</div>
  {/if}

  {#if failed}
    <div class="drawer-state" role="alert">
      <strong>History is taking a moment</strong>
      <p>Your live bridge view is unaffected.</p>
      <button class="text-button" type="button" onclick={() => void load()}>Try again</button>
    </div>
  {/if}

  <div class="history-sentinel" bind:this={sentinel} aria-hidden="true"></div>
  {#if hasMore && !loading}
    <button class="load-more" type="button" onclick={() => void load()}>Load earlier movements</button>
  {:else if !hasMore && events.length > 0}
    <p class="history-end">You’ve reached the available history.</p>
  {/if}
</DrawerShell>
